import { Renderer2 } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';
import { StorageService } from './storage.service';
import { ArrayHelper } from '../utils/array-helper';
import * as i0 from "@angular/core";
import * as i1 from "@ngx-translate/core";
import * as i2 from "./storage.service";
import * as i3 from "@angular/platform-browser";
var LanguageService = /** @class */ (function () {
    function LanguageService(translate, storage, doc) {
        this.translate = translate;
        this.storage = storage;
        this.doc = doc;
    }
    /**
     * Returns the best machting language from the given set of languages
     * according to the users browser settings
     *
     * @static
     * @param {string[]} availableLanguages
     * @returns {string}
     * @memberof LanguageHelpers
     */
    LanguageService.GetBestClientLanguage = function (availableLanguages) {
        var preferredLanguages = this.GetNavigatorLanguages();
        // no preferences
        if (preferredLanguages === null) {
            return null;
        }
        var _loop_1 = function (i) {
            var lang = preferredLanguages[i].toLowerCase();
            // find available locales for given language
            var locales = availableLanguages.filter(function (l) {
                return l.substr(0, 2).toLowerCase() === lang.substr(0, 2);
            });
            // next if no match
            if (ArrayHelper.isNullOrEmpty(locales)) {
                return "continue";
            }
            // try to find exact locale
            var exactMatches = locales.filter(function (l) { return l.toLowerCase() === lang; });
            if (ArrayHelper.hasAny(exactMatches)) {
                return { value: exactMatches[0] };
            }
            return { value: locales[0] };
        };
        for (var i = 0; i < preferredLanguages.length; i++) {
            var state_1 = _loop_1(i);
            if (typeof state_1 === "object")
                return state_1.value;
        }
        return null;
    };
    /**
     * Returns a list of browser prefered laguages
     *
     * @private
     * @static
     * @returns {(ReadonlyArray<string> | null)}
     * @memberof LanguageService
     */
    LanguageService.GetNavigatorLanguages = function () {
        var langs = window.navigator.languages;
        if (langs === undefined) {
            var ieLang = window.navigator.userLanguage;
            if (ieLang !== undefined) {
                // ie...
                langs = [window.navigator.userLanguage];
            }
            else {
                return null;
            }
        }
        return langs;
    };
    /**
     * Get the currently selected uiLanguage
     *
     * @returns
     * @memberof LanguageService
     */
    LanguageService.prototype.getLanguage = function () {
        return this.storage.getUiCulture();
    };
    /**
     * Set the uiLanguage
     *
     * @param {string} language
     * @param {Renderer2} renderer
     * @memberof LanguageService
     */
    LanguageService.prototype.setLanguage = function (language, renderer) {
        // save choosen language
        this.storage.setUiCulture(language);
        // set language on html-element
        renderer.setProperty(this.doc.documentElement, 'lang', language);
        // the lang to use, if the lang isn't available, it will use the current loader to get them
        this.translate.use(language);
        this.currentLanguage = language;
        moment.locale(this.currentLanguage);
    };
    /**
     * Parses a current-local date-string to a Date object
     *
     * @param {string} value
     * @returns {Date}
     * @memberof LanguageService
     */
    LanguageService.prototype.parseDate = function (value) {
        return moment(value, 'L').toDate();
    };
    /**
     * Returns the date representation according to the current local (eg. 22.10.1989)
     *
     * @param {Date} date
     * @returns {string}
     * @memberof LanguageService
     */
    LanguageService.prototype.formatDate = function (date) {
        if (!date) {
            return '';
        }
        return moment(date).format('L');
    };
    /**
     * Returns the time representation according to the current local (eg. 16:45 or 4:45 PM)
     *
     * @param {Date} date
     * @returns {string}
     * @memberof LanguageService
     */
    LanguageService.prototype.formatTime = function (date) {
        if (!date) {
            return '';
        }
        return moment(date).format('LT');
    };
    /**
     * Returns the name of the weekday in the current local (eg. Freitag)
     *
     * @param {Date} date
     * @returns
     * @memberof LanguageService
     */
    LanguageService.prototype.formatWeekday = function (date) {
        return moment(date).format('dddd');
    };
    /**
     * Returns the abbreviated name of the weekday in the current local (eg. Fr.)
     *
     * @param {Date} date
     * @returns
     * @memberof LanguageService
     */
    LanguageService.prototype.formatWeekdayShort = function (date) {
        return moment(date).format('ddd');
    };
    LanguageService.prototype.getWeekNumber = function (date) {
        return moment(date).format('w');
    };
    LanguageService.prototype.getMonthString = function (date) {
        return moment(date).format('MMMM');
    };
    /**
     * Returns the short name of a weekday (eg. Mo)
     *
     * @param {number} weekday
     * @returns {string}
     * @memberof LanguageService
     */
    LanguageService.prototype.getWeekdayShortName = function (weekday) {
        return moment.weekdaysShort(weekday);
    };
    /**
     * Returns the full name of a weekday (eg. Montag)
     *
     * @param {number} weekday
     * @returns {string}
     * @memberof LanguageService
     */
    LanguageService.prototype.getWeekdayFullName = function (weekday) {
        return moment.weekdays(weekday);
    };
    /**
     * Returns the short name of a month (eg. Apr.)
     *
     * @param {number} month
     * @param {number} [year]
     * @returns {string}
     * @memberof LanguageService
     */
    LanguageService.prototype.getMonthShortName = function (month, year) {
        return moment.monthsShort(month - 1);
    };
    /**
     * Returns the full name of a month (eg. Juni)
     *
     * @param {number} month
     * @returns {string}
     * @memberof LanguageService
     */
    LanguageService.prototype.getMonthFullName = function (month) {
        return moment.months(month - 1);
    };
    /**
     * Returns date as string (eg. Donnerstag, 20. Juni 2019)
     *
     * @param {Date} date
     * @returns {string}
     * @memberof LanguageService
     */
    LanguageService.prototype.getDayAriaLabel = function (date) {
        return moment(date).format('dddd, LL');
    };
    /**
     * Returns the representation of a date according to the current local (eg. mm/dd/yyyy)
     *
     * @returns {Promise<string>}
     * @memberof LanguageService
     */
    LanguageService.prototype.getDateFormat = function () {
        var _this = this;
        var that = this;
        return new Promise(function (resolve, reject) {
            if (that.currentDateFormat !== undefined) {
                resolve(that.currentDateFormat);
                return;
            }
            _this.translate.get('YEAR_CHAR').subscribe(function (y) {
                _this.translate.get('MONTH_CHAR').subscribe(function (m) {
                    _this.translate.get('DAY_CHAR')
                        .toPromise()
                        .then(function (d) {
                        var format = moment(new Date(1999, 11, 31)).format('L');
                        that.currentDateFormat = format.replace('1999', y + y + y + y).replace('12', m + m).replace('31', d + d);
                        resolve(that.currentDateFormat);
                        return;
                    });
                });
            });
        });
    };
    LanguageService.ngInjectableDef = i0.defineInjectable({ factory: function LanguageService_Factory() { return new LanguageService(i0.inject(i1.TranslateService), i0.inject(i2.StorageService), i0.inject(i3.DOCUMENT)); }, token: LanguageService, providedIn: "root" });
    return LanguageService;
}());
export { LanguageService };
